import React, { useMemo } from "react";
import { LineChart as MuiLineChart } from "@mui/x-charts/LineChart";
import type { ChartsTextStyle } from "@mui/x-charts/ChartsText";
import styles from "./Charts.module.scss";
import { formatCompactNumber, formatFullNumber } from "./chartUtils";

export type ChartDatum = {
  label: string;
  value: number;
};

type LineAreaChartProps = {
  data: ChartDatum[];
  lineColor: string;
  areaColor: string;
  title?: string;
};

const chartHeight = 320;
const axisLabelStyle: ChartsTextStyle = {
  fill: "var(--muted)",
  fontSize: 12,
  fontWeight: 500,
};

const chartSx = {
  "--Charts-grid-color": "rgba(15, 23, 42, 0.08)",
  "--Charts-axis-lineColor": "rgba(15, 23, 42, 0.12)",
  "--Charts-axis-tickColor": "rgba(15, 23, 42, 0.38)",
  "--Charts-tooltip-bg": "var(--surface, #fff)",
  "--Charts-tooltip-text": "var(--text, #0f172a)",
} as const;

function LineAreaChart({
  data,
  lineColor,
  areaColor,
  title,
}: LineAreaChartProps) {
  const ariaLabel = title ?? "Line chart";
  const chartData = useMemo(
    () => ({
      labels: data.map((datum) => datum.label),
      values: data.map((datum) => datum.value),
    }),
    [data]
  );

  return (
    <figure className={styles.chartFigure}>
      {title && <figcaption className={styles.title}>{title}</figcaption>}
      <div className={styles.chart}>
        <MuiLineChart
          aria-label={ariaLabel}
          height={chartHeight}
          xAxis={[
            {
              id: "months",
              scaleType: "point",
              data: chartData.labels,
              tickLabelStyle: axisLabelStyle,
            },
          ]}
          yAxis={[
            {
              id: "values",
              tickLabelStyle: axisLabelStyle,
              valueFormatter: (value: number | null) =>
                formatCompactNumber(
                  typeof value === "number" ? value : Number(value)
                ),
            },
          ]}
          series={[
            {
              id: title ?? "line-series",
              data: chartData.values,
              color: lineColor,
              label: title,
              area: true,
              curve: "monotoneX",
              showMark: true,
              valueFormatter: (value: number | null) => formatFullNumber(value),
            },
          ]}
          margin={{ top: 16, right: 16, bottom: 32, left: 32 }}
          grid={{ horizontal: true, vertical: false }}
          hideLegend
          slotProps={{
            area: {
              style: {
                fill: areaColor,
                fillOpacity: 0.25,
              },
            },
            line: {
              style: {
                strokeWidth: 3,
              },
            },
            mark: {
              style: {
                stroke: "#fff",
                strokeWidth: 2,
              },
            },
          }}
          sx={chartSx}
        />
      </div>
    </figure>
  );
}

export default LineAreaChart;
